// frontend/src/components/mice/MouseForm.js
// Slide-out panel body for adding and editing mice.
//
// Presentation only, following the MiceTable / ExcelImport precedent: every
// piece of state and every handler lives in MiceManagement and arrives as a
// prop. The quick-add save handlers in particular stay in the parent — they
// POST, refresh the genetics options and raise the outcome toast, which is
// orchestration, not form rendering. The one message this component renders is
// `error`: field validation, which must persist beside the field it is about.
//
// The strain / locus / allele quick-adds were three near-identical blocks;
// they are one QuickAddSelect below, used three times.

import React from 'react';
import { getLocalToday } from '../../utils/dateHelpers';

// A <select> that can flip into a "type a new name and save it" row.
function QuickAddSelect({
  isAdding,
  onStartAdd,
  onCancelAdd,
  newName,
  setNewName,
  onSave,
  isSaving,
  placeholder,
  selectName,
  selectValue,
  onSelectChange,
  emptyLabel,
  options,
  wrapperStyle,
}) {
  const rowStyle = { display: 'flex', gap: '10px', ...wrapperStyle };
  const smallButton = { flexShrink: 0, padding: '0.25rem 0.5rem', fontSize: '0.8rem' };

  if (isAdding) {
    return (
      <div style={rowStyle}>
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder={placeholder}
          style={{ flex: 1 }}
        />
        <button type="button" onClick={onSave} className="quarternary-button" style={smallButton} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save'}
        </button>
        <button type="button" onClick={onCancelAdd} className="quarternary-cancel-button" style={smallButton}>
          X
        </button>
      </div>
    );
  }

  return (
    <div style={rowStyle}>
      <select name={selectName} value={selectValue} onChange={onSelectChange} style={{ flex: 1 }}>
        <option value="">{emptyLabel}</option>
        {options.map((opt) => (
          <option key={opt.key} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <button type="button" onClick={onStartAdd} className="tertiary-button" style={smallButton}>
        Add New
      </button>
    </div>
  );
}

function MouseForm({
  // mode
  isEditing,
  editedMouseId,
  editingMouseArchived,
  // form data
  mouseFormData,
  handleInputChange,
  handleGenotypeSelectChange,
  // cage assignment (new mice only)
  cages,
  cageSearchRef,
  cageSearchQuery,
  setCageSearchQuery,
  showCageDropdown,
  setShowCageDropdown,
  sharedCageId,
  setSharedCageId,
  showNewCageForm,
  setShowNewCageForm,
  newCage,
  setNewCage,
  onCreateCage,
  isCreatingCage,
  // options
  strains,
  locusOptions,
  alleleOptions,
  // quick-adds
  isAddingStrain,
  setIsAddingStrain,
  newStrainName,
  setNewStrainName,
  onSaveNewStrain,
  isAddingLocus,
  setIsAddingLocus,
  newLocusName,
  setNewLocusName,
  onSaveNewLocus,
  isAddingAllele,
  setIsAddingAllele,
  newAlleleName,
  setNewAlleleName,
  onSaveNewAllele,
  isSavingQuickItem,
  // genotype pairs
  onAddGenotypePair,
  onRemoveGenotypePair,
  // submit / cancel
  onSubmit,
  isSubmitting,
  onCancel,
  // feedback
  error,
}) {
  const query = cageSearchQuery.toLowerCase();
  const filteredCages = cages.filter(
    (cage) =>
      cage.display_cage_number?.toLowerCase().includes(query) ||
      cage.location?.toLowerCase().includes(query)
  );

  // Prefer the live mice_in_cage array when the record carries one; fall back
  // to the denormalized count.
  const occupancy = (cage) =>
    Array.isArray(cage.mice_in_cage) ? cage.mice_in_cage.length : Number(cage.mouse_count) || 0;
  const cageLabel = (cage) =>
    `${cage.display_cage_number} - ${cage.location} (${occupancy(cage)}/${cage.capacity})`;

  const submitLabel = isEditing
    ? `Update ${Array.isArray(editedMouseId) ? editedMouseId.length : '1'} Mouse(s)`
    : `Add ${
        Math.max(1, parseInt(mouseFormData.copies, 10) || 1) *
        Math.max(1, mouseFormData.physical_tag_input.split(',').filter((t) => t.trim()).length || 1)
      } Mouse(s)`;

  return (
    <div className="add-mouse-section">
      <h2>
        {isEditing ? `Edit Mouse: ${mouseFormData.physical_tag_input || editedMouseId}` : `Add New Mouse(s)`}
      </h2>
      {editingMouseArchived && (
        <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px', padding: '12px', marginBottom: '16px', color: '#856404', textAlign: 'center', fontWeight: 600 }}>
          This mouse is archived. Unarchive it to make changes.
        </div>
      )}
      <form onSubmit={onSubmit} className="add-mouse-form">
        <fieldset disabled={editingMouseArchived} style={{ border: 'none', padding: 0, margin: 0 }}>
          {!isEditing && (
            <div className="form-group" style={{ border: '1px solid #007bff', padding: '15px', borderRadius: '8px', marginBottom: '20px', background: '#e7f3ff' }}>
              <label style={{ fontWeight: 'bold', color: '#007bff' }}>Assign Mouse(s) to Cage:</label>
              {!showNewCageForm ? (
                <>
                  <div ref={cageSearchRef} style={{ position: 'relative' }}>
                    <input
                      type="text"
                      placeholder="Search cage number or location..."
                      value={cageSearchQuery}
                      onChange={(e) => {
                        setCageSearchQuery(e.target.value);
                        setSharedCageId('');
                        setShowCageDropdown(true);
                      }}
                      onFocus={() => setShowCageDropdown(true)}
                      autoComplete="off"
                    />
                    {showCageDropdown && (
                      <ul className="cage-search-dropdown">
                        {filteredCages.length > 0 ? (
                          filteredCages.map((cage) => (
                            <li
                              key={cage.id}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setSharedCageId(cage.id);
                                setCageSearchQuery(cageLabel(cage));
                                setShowCageDropdown(false);
                              }}
                              className={cage.id === sharedCageId ? 'selected' : ''}
                            >
                              {cageLabel(cage)}
                            </li>
                          ))
                        ) : (
                          <li className="empty">No cages found</li>
                        )}
                      </ul>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowNewCageForm(true)}
                    className="secondary-button"
                    style={{ marginTop: '8px' }}
                  >
                    New Cage
                  </button>
                </>
              ) : (
                <div style={{ border: '1px solid #007bff', padding: '10px', borderRadius: '4px', marginTop: '5px' }}>
                  <h4 style={{ margin: '0 0 10px 0' }}>Create New Cage</h4>
                  <input
                    type="text"
                    placeholder="Cage number"
                    value={newCage.display_cage_number}
                    onChange={(e) => setNewCage({ ...newCage, display_cage_number: e.target.value })}
                    style={{ marginBottom: '10px' }}
                    required
                  />
                  <input
                    type="text"
                    placeholder="Location"
                    value={newCage.location}
                    onChange={(e) => setNewCage({ ...newCage, location: e.target.value })}
                    style={{ marginBottom: '10px' }}
                  />
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      type="button"
                      onClick={onCreateCage}
                      className="primary-button"
                      style={{ flex: 1 }}
                      disabled={isCreatingCage}
                    >
                      {isCreatingCage ? 'Creating...' : 'Create'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowNewCageForm(false);
                        setNewCage({ display_cage_number: '', location: '' });
                      }}
                      className="secondary-button"
                      style={{ flex: 1 }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div style={{ border: '1px solid #eee', padding: '15px', marginBottom: '20px', borderRadius: '8px', background: '#fcfcfc' }}>
            <h4>Mouse Details</h4>
            <div className="form-group">
              <label>Physical Tag(s) (Optional)</label>
              <input
                type="text"
                name="physical_tag_input"
                value={mouseFormData.physical_tag_input}
                onChange={handleInputChange}
                placeholder={
                  isEditing
                    ? Array.isArray(editedMouseId)
                      ? 'Multiple Values'
                      : 'e.g., Blue-23'
                    : 'Comma-separated tags (e.g., Blue-23, Green-1)'
                }
                disabled={Array.isArray(editedMouseId)}
              />
            </div>

            <div className="form-group">
              <label>Sex *</label>
              <select name="sex" value={mouseFormData.sex} onChange={handleInputChange} required>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>

            <div className="form-group">
              <label>Strain</label>
              <QuickAddSelect
                isAdding={isAddingStrain}
                onStartAdd={() => setIsAddingStrain(true)}
                onCancelAdd={() => {
                  setIsAddingStrain(false);
                  setNewStrainName('');
                }}
                newName={newStrainName}
                setNewName={setNewStrainName}
                onSave={onSaveNewStrain}
                isSaving={isSavingQuickItem}
                placeholder="New Strain Name"
                selectName="strain_id"
                selectValue={mouseFormData.strain_id}
                onSelectChange={handleInputChange}
                emptyLabel="Select Strain"
                options={strains.map((s) => ({ key: s.id, value: s.id, label: s.strain_name }))}
              />
            </div>

            <div className="form-group">
              <label>Add Genotype Pair</label>
              <QuickAddSelect
                isAdding={isAddingLocus}
                onStartAdd={() => setIsAddingLocus(true)}
                onCancelAdd={() => {
                  setIsAddingLocus(false);
                  setNewLocusName('');
                }}
                newName={newLocusName}
                setNewName={setNewLocusName}
                onSave={onSaveNewLocus}
                isSaving={isSavingQuickItem}
                placeholder="New Locus Name"
                selectName="currentLocusSelection"
                selectValue={mouseFormData.currentLocusSelection}
                onSelectChange={handleGenotypeSelectChange}
                emptyLabel="Select Locus"
                options={locusOptions.map((l) => ({ key: l.id, value: l.locus_name, label: l.locus_name }))}
                wrapperStyle={{ marginBottom: '10px' }}
              />
              <QuickAddSelect
                isAdding={isAddingAllele}
                onStartAdd={() => setIsAddingAllele(true)}
                onCancelAdd={() => {
                  setIsAddingAllele(false);
                  setNewAlleleName('');
                }}
                newName={newAlleleName}
                setNewName={setNewAlleleName}
                onSave={onSaveNewAllele}
                isSaving={isSavingQuickItem}
                placeholder="New Allele Name"
                selectName="currentAlleleSelection"
                selectValue={mouseFormData.currentAlleleSelection}
                onSelectChange={handleGenotypeSelectChange}
                emptyLabel="Select Allele"
                options={alleleOptions.map((a) => ({ key: a.id, value: a.allele_name, label: a.allele_name }))}
                wrapperStyle={{ marginBottom: '10px' }}
              />
              <button
                type="button"
                onClick={onAddGenotypePair}
                style={{ width: '100%', padding: '8px 15px', marginTop: '5px' }}
                className="primary-button"
              >
                Add Genotype Pair to Mouse
              </button>
            </div>

            {mouseFormData.genotype_data.length > 0 && (
              <div style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '10px', marginTop: '10px' }}>
                <h4>Selected Genotype Pairs:</h4>
                <ul>
                  {mouseFormData.genotype_data.map((pair, pairIndex) => (
                    <li key={pairIndex} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                      <span>
                        {pair.locus_name} ({pair.allele_name})
                      </span>
                      <button
                        type="button"
                        onClick={() => onRemoveGenotypePair(pairIndex)}
                        style={{ background: '#dc3545', color: 'white', border: 'none', borderRadius: '3px', padding: '3px 8px', fontSize: '0.8em', cursor: 'pointer', width: 'auto' }}
                      >
                        Remove{' '}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="form-group">
              <label>Date of Birth *</label>
              <input
                type="date"
                name="date_of_birth"
                value={mouseFormData.date_of_birth}
                onChange={handleInputChange}
                required={!isEditing}
                max={getLocalToday()}
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            </div>

            {!isEditing && (
              <div className="form-group">
                <label>Copies</label>
                <input
                  type="number"
                  name="copies"
                  value={mouseFormData.copies}
                  onChange={handleInputChange}
                  placeholder="1"
                  min="1"
                  max="10"
                  required
                />
              </div>
            )}

            <div className="form-group">
              <label>Notes (Optional)</label>
              <textarea
                name="notes"
                value={mouseFormData.notes}
                onChange={handleInputChange}
                placeholder="Any notes about this mouse"
              />
            </div>
          </div>
        </fieldset>

        {error && <div className="error">{error}</div>}

        <button type="submit" className="primary-button" disabled={isSubmitting || editingMouseArchived}>
          {isSubmitting ? 'Saving...' : submitLabel}
        </button>

        <button type="button" onClick={onCancel} className="secondary-button" style={{ marginTop: '10px' }}>
          Cancel
        </button>
      </form>
    </div>
  );
}

export default MouseForm;
